// de-DE number and unit formatting for the STE UI.
// A narrow no-break space separates value and unit so values never wrap.

const LOCALE = 'de-DE';
const NBSP = ' ';

const numberFormats = new Map<string, Intl.NumberFormat>();

function numberFormat(minFractionDigits: number, maxFractionDigits: number): Intl.NumberFormat {
  const key = `${minFractionDigits}:${maxFractionDigits}`;
  let format = numberFormats.get(key);
  if (!format) {
    format = new Intl.NumberFormat(LOCALE, {
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits,
    });
    numberFormats.set(key, format);
  }
  return format;
}

export function formatNumber(value: number, fractionDigits = 0): string {
  return numberFormat(fractionDigits, fractionDigits).format(roundTo(value, fractionDigits));
}

/** Formats a power value given in watts, switching to kW from 1000 W upwards. */
export function formatPower(watts: number): string {
  const abs = Math.abs(watts);
  if (abs < 1000) {
    return `${formatNumber(Math.round(watts))}${NBSP}W`;
  }
  return `${formatNumber(watts / 1000, abs < 10_000 ? 1 : 0)}${NBSP}kW`;
}

/** Formats an energy value given in kWh. */
export function formatEnergy(kWh: number): string {
  const abs = Math.abs(kWh);
  if (abs >= 1000) {
    return `${formatNumber(kWh / 1000, 2)}${NBSP}MWh`;
  }
  return `${formatNumber(kWh, abs < 10 ? 2 : 1)}${NBSP}kWh`;
}

export function formatPercent(percent: number, fractionDigits = 0): string {
  return `${formatNumber(percent, fractionDigits)}${NBSP}%`;
}

export function formatTemperature(celsius: number): string {
  return `${formatNumber(celsius, 1)}${NBSP}°C`;
}

const currencyFormat = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: 'EUR' });

export function formatCurrency(euros: number): string {
  return currencyFormat.format(roundTo(euros, 2));
}

/** Formats an energy price given in EUR/kWh as ct/kWh. */
export function formatPricePerKWh(eurosPerKWh: number): string {
  return `${formatNumber(eurosPerKWh * 100, 1)}${NBSP}ct/kWh`;
}

const TIME_OPTIONS: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };
const DATE_OPTIONS: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };

export function formatTime(date: Date, timeZone?: string): string {
  return new Intl.DateTimeFormat(LOCALE, { ...TIME_OPTIONS, timeZone }).format(date);
}

export function formatDate(date: Date, timeZone?: string): string {
  return new Intl.DateTimeFormat(LOCALE, { ...DATE_OPTIONS, timeZone }).format(date);
}

// rounds half away from zero and avoids "-0" for values that round to zero
function roundTo(value: number, fractionDigits: number): number {
  const factor = 10 ** fractionDigits;
  const rounded = (Math.sign(value) * Math.round(Math.abs(value) * factor)) / factor;
  return rounded === 0 ? 0 : rounded;
}
